import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Filters from "../layout/Filters";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { getColour } from "../../utils/colours";
import Badge from "../layout/Badge";
import Table from "../layout/Table";
import { translateText } from "../../utils/translation";
import { getWebhookHistory } from "../../slices/integration";

export default function History() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const pageNum =
    searchParams.get("page") === null
      ? 1
      : parseInt(searchParams.get("page") as string);
  const searchAction = searchParams.get("action");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getWebhookHistory({
        page: pageNum,
        action: searchAction,
        dateRange: dateRangeTerm,
      }),
    );
  }, [dispatch, pageNum, dateRangeTerm, searchAction]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [action, setAction] = useState({ value: "" });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    webhooks: { history },
    options,
  } = useAppSelector((state) => state.integration);
  const language = user === null ? "EN" : user.language;

  const historyData = history.data === null ? [] : history.data;
  const webhookActionOptions =
    options.actions === undefined
      ? []
      : Object.keys(options.actions).map((key) => ({
          value: key,
          label: options.actions[key][language],
        }));

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("webhook_history", language)} />

      {/* Loading spinner */}
      {history.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "action",
                  name: translateText("action", language),
                  options: webhookActionOptions,
                },
              ]}
              queryParams={[
                ["action", searchAction],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                action: {
                  state: action,
                  stateName: "value",
                  onChange: setAction,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={history.loading}
              baseUrl="/integration/history"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {historyData.length > 0 ? (
              <>
                {/* History table */}
                <Table
                  header={
                    <tr>
                      <th className="px-2 py-3">
                        {translateText("created", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("name", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("action", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("destination", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("sent_body", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("response_on_send", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("received_body", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("status", language)}
                      </th>
                    </tr>
                  }
                  body={historyData.map((hist) => (
                    <tr key={hist.id} className="hover:bg-light/20">
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.datetime_created}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.webhook}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={hist.action[1]}
                          bgColour={getColour(hist.action[0])}
                          textColour={`${getColour(hist.action[0])}-dark`}
                          ringColour={`${getColour(hist.action[0])}-dark`}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.integration_entity}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.sent_body}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.response_on_send}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {hist.received_body}
                      </td>
                      <td className="px-2 py-3">
                        <Badge
                          text={hist.status[1]}
                          bgColour={getColour(hist.status[0])}
                          textColour={`${getColour(hist.status[0])}-dark`}
                          ringColour={`${getColour(hist.status[0])}-dark`}
                        />
                      </td>
                    </tr>
                  ))}
                  paginationHasPrevious={history.pagination.has_previous}
                  paginationHasNext={history.pagination.has_next}
                  paginationCurrentPage={history.pagination.current_page}
                  paginationTotalPages={history.pagination.total_pages}
                  paginationTotalObjects={history.pagination.total_objects}
                  paginationQueryParams={["action", "dateRange"]}
                />
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_webhook_history", language)}
                subtext={translateText("no_webhook_history_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
